/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React from 'react';
import Map from '@mui/icons-material/Map';
import { StyledToggleButtonGroup, StyledToggleButton } from '../toggle_group/ToggleButton';

export const MIN_MAP_HEIGHT = 386; // px

export enum MapSizesEnum {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}
export type MapSizesType = keyof typeof MapSizesEnum;

export interface MapSizeDetails {
  position: {
    top: number; // px
    left: number; // px
  };
  iconSize: number; // px
  mapHeight: number; // vh
}

const MapSizes: { [key in MapSizesType]: MapSizeDetails } = {
  SMALL: {
    position: {
      top: 83,
      left: 17,
    },
    iconSize: 14,
    mapHeight: 39,
  },
  MEDIUM: {
    position: {
      top: 81,
      left: 52,
    },
    iconSize: 20,
    mapHeight: 50,
  },
  LARGE: {
    position: {
      top: 78,
      left: 90,
    },
    iconSize: 28,
    mapHeight: 70,
  },
};

export const getMapHeight = (mapSize: MapSizesEnum): number => {
  const mapHeightStaticByResolution = (window.innerHeight * MapSizes[mapSize].mapHeight) / 100;

  if (mapHeightStaticByResolution < MIN_MAP_HEIGHT) {
    return MIN_MAP_HEIGHT;
  } else {
    return mapHeightStaticByResolution;
  }
};

interface MapSizeControllerProps {
  selectedValue: MapSizesEnum;
  setSelectedValue: (newSelectedValue: MapSizesEnum, mapHeight: number) => void;
}

const MapSizeController = (props: MapSizeControllerProps) => {
  const { selectedValue, setSelectedValue } = props;

  const handleSelectionChange = (event: React.MouseEvent<HTMLElement>, newValue: MapSizesEnum) => {
    // The onChange event of the ToggleButtonGroup is fired even when the same ToggleButton is already selected.
    // In that case the newValue argument is null.
    // For this reason, we set the same map size and map height again
    // to update the size oin case the user resized the window.
    if (newValue !== null) {
      setSelectedValue(newValue, getMapHeight(newValue));
    } else {
      setSelectedValue(selectedValue, getMapHeight(selectedValue));
    }
  };

  return (
    <StyledToggleButtonGroup exclusive value={selectedValue} onChange={handleSelectionChange}>
      {Object.keys(MapSizesEnum).map((size: string) => (
        <StyledToggleButton
          key={size}
          value={size}
          disableRipple
          disableFocusRipple
          style={{ top: MapSizes[size].position.top, left: MapSizes[size].position.left }}
        >
          <Map style={{ width: MapSizes[size].iconSize, height: MapSizes[size].iconSize }} />
        </StyledToggleButton>
      ))}
    </StyledToggleButtonGroup>
  );
};

export default MapSizeController;
